import React, { useEffect, useState } from 'react';
import { Book } from '@/utils/bookRouting';
import { useBookCoverVideo } from '@/hooks/useBookCoverVideo';
import { useTheme } from '@/contexts/ThemeContext';

interface CategoryBookProps {
  book: Book;
  onBookClick: (book: Book) => void;
  showCover?: boolean;
  onCoverReady?: (bookKey: string) => void;
}

const CategoryBook: React.FC<CategoryBookProps> = ({ book, onBookClick, showCover = true, onCoverReady }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const {
    videoRef,
    showVideo,
    handleMouseEnter,
    handleMouseLeave,
    coverVideoPath,
  } = useBookCoverVideo(book.coverAnimationUrl);

  useEffect(() => {
    if (isImageLoaded && onCoverReady) {
      onCoverReady(book.id);
    }
  }, [book.id, isImageLoaded, onCoverReady]);

  return (
    <div
      className="group relative h-58 w-[155px] cursor-pointer transition-transform duration-200 hover:-translate-y-1"
      onClick={() => onBookClick(book)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {!showCover ? (
        <div
          className={`absolute inset-0 animate-pulse rounded-sm ${
            isDarkMode ? 'bg-gray-800' : 'bg-gray-200'
          }`}
          aria-hidden="true"
        />
      ) : null}
      <img
        src={book.coverUrl}
        alt={`${book.title} by ${book.author} - Book Cover`}
        className="absolute inset-0 h-full w-full shadow-xl object-contain transition-opacity duration-500"
        onLoad={() => setIsImageLoaded(true)}
        onError={() => setIsImageLoaded(true)}
        style={{
          boxShadow: '5px 10px 15px rgba(0,0,0,0.4)',
          opacity: showCover ? 1 : 0
        }}
      />
      {coverVideoPath && (
        <video
          ref={videoRef}
          src={coverVideoPath}
          className="absolute inset-0 h-full w-full shadow-xl object-contain transition-opacity duration-500"
          style={{
            boxShadow: '5px 12px 22px rgba(0,0,0,0.32)',
            opacity: showCover && showVideo ? 1 : 0,
            pointerEvents: showVideo ? 'auto' : 'none'
          }}
          preload="metadata"
          loop
          muted
          playsInline
          aria-label={`${book.title} by ${book.author} - Animated Book Cover`}
        >
          Your browser does not support the video tag.
        </video>
      )}
    </div>
  );
};

export default CategoryBook;
